<template>
  <div class="table-container">
    <el-table :data="data" v-loading="loading" style="width: 100%" border>
      <el-table-column prop="id" :label="$t('MemberRegistrationReview.serialNumber')" width="80" align="center">
        <template #default="scope">
          <span 
            style="cursor: pointer; color: #409EFF; text-decoration: none;"
            @click="handleView(scope.row)">
            {{ scope.row.id }}
          </span>
        </template>
      </el-table-column>
      
      <el-table-column prop="contactName" :label="$t('MemberRegistrationReview.contactName')" width="120" align="center"/>
      
      <el-table-column prop="storeName" :label="$t('MemberRegistrationReview.storeName')" min-width="150" show-overflow-tooltip/>
      
      <el-table-column prop="email" :label="$t('MemberRegistrationReview.email')" min-width="160" show-overflow-tooltip/>
      
      <el-table-column prop="tel" :label="$t('MemberRegistrationReview.tel')" width="130" align="center"/>
      
      <el-table-column :label="$t('MemberRegistrationReview.country')" width="100" align="center">
        <template #default="scope">
          {{ getCountryName(scope.row.country) }}
        </template>
      </el-table-column>
      
      <el-table-column :label="$t('MemberRegistrationReview.customerType')" width="100" align="center">
        <template #default="scope">
          <el-tag :type="Number(scope.row.is_upgrade) === 1 ? 'success' : 'info'" size="small">
            {{ Number(scope.row.is_upgrade) === 1 ? $t('MemberRegistrationReview.upgrade') : $t('MemberRegistrationReview.guest') }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="applicantStatus" :label="$t('MemberRegistrationReview.applicantStatus')" width="120" align="center">
        <template #default="scope">
          <el-tag :type="getStatusType(scope.row.applicantStatus)">
            {{ scope.row.statusText }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="applyTime" :label="$t('MemberRegistrationReview.applyTime')" width="180" align="center">
        <template #default="scope">
          {{ formatDateTime(scope.row.applyTime) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewer" :label="$t('MemberRegistrationReview.reviewer')" width="120" align="center">
        <template #default="scope">
          {{ scope.row.reviewer || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewTime" :label="$t('MemberRegistrationReview.reviewTime')" width="180" align="center">
        <template #default="scope">
          {{ formatDateTime(scope.row.reviewTime) || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewComment" :label="$t('MemberRegistrationReview.reviewComment')" min-width="150" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.reviewComment || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column :label="$t('MemberRegistrationReview.dealerRating')" width="150" align="center">
        <template #default="scope">
          <div class="dealer-rating">
            <span 
              v-for="i in 5" 
              :key="i" 
              class="star"
              :class="i <= getDealerRating(scope.row) ? 'star-gold' : 'star-gray'"
              @click="handleDealerRatingChange(scope.row, i)"
            >
              ★
            </span>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog 
      :model-value="dialogVisible" 
      :title="$t('MemberRegistrationReview.reviewApplication')" 
      width="900px" 
      :show-close="true" 
      @close="closeDialog">
      <div v-loading="dialogLoading">
      <el-form :model="reviewForm" :rules="reviewRules" ref="reviewFormRef" label-width="180px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.contactName')">
              <el-input v-model="currentApplication.contactName" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.storeName')">
              <el-input v-model="currentApplication.storeName" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.email')">
              <el-input v-model="currentApplication.email" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.tel')">
              <el-input v-model="currentApplication.tel" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.country')">
              <el-input :value="getCountryName(currentApplication.country)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.location')">
              <el-input v-model="currentApplication.location" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.postCode')">
              <el-input v-model="currentApplication.postCode" disabled />
              <span v-if="!postCodeValidation.valid" style="color: #F56C6C; font-size: 12px; margin-top: 4px; display: block;">{{ postCodeValidation.message }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.taxNumber')">
              <el-input v-model="currentApplication.taxNumber" disabled />
              <div class="vat-buttons">
                <el-button type="success" size="small" @click="openVatLink('eu')">{{ $t('MemberRegistrationReview.vatValidationEu') }}</el-button>
                <el-button type="success" size="small" @click="openVatLink('nonEu')">{{ $t('MemberRegistrationReview.vatValidationNonEu') }}</el-button>
                <el-button type="success" size="small" @click="openVatLink('uk')">{{ $t('MemberRegistrationReview.vatValidationUk') }}</el-button>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.website')">
              <el-input v-model="currentApplication.website" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.businessLicense')">
              <div v-if="currentApplication.businessLicense" class="license-files">
                <el-link 
                  v-for="(license, index) in getBusinessLicenseList(currentApplication.businessLicense)" 
                  :key="index"
                  @click="previewBusinessLicense(license)" 
                  type="primary" 
                  :underline="false" 
                  style="cursor: pointer; display: block; margin-bottom: 4px;"
                >
                  <span style="margin-right: 8px;">{{ index + 1 }}.</span>
                  {{ getBusinessLicenseFileName(license) }}
                </el-link>
              </div>
              <span v-else>-</span>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.workWithAnanda')">
              <el-input :value="getWorkWithAnandaText(currentApplication)" disabled />
            </el-form-item>
          </el-col>
          <template v-if="getWorkWithAnandaText(currentApplication) === $t('MemberRegistrationReview.yes')">
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.howKnowAnanda')">
                <el-input :value="getHowKnowText(currentApplication.howKnowAnanda)" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.whyChooseAnanda')">
                <el-input :value="getWhyChooseText(currentApplication.whyChooseAnanda)" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.additionalInfo')">
                <el-input v-model="currentApplication.additionalInfo" type="textarea" :rows="3" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.subscribeNewsletter')">
                <el-input :value="currentApplication.subscribeNewsletter === true || currentApplication.subscribeNewsletter === 'yes' ? $t('MemberRegistrationReview.yes') : $t('MemberRegistrationReview.no')" disabled />
              </el-form-item>
            </el-col>
          </template>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.officeArea')">
              <el-input :value="getAreaText(currentApplication.officeArea)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.warehouse')">
              <el-input :value="getAreaText(currentApplication.warehouse)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.staff')">
              <el-input :value="getStaffText(currentApplication.staff)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.serviceCapability')">
              <el-input :value="getServiceCapabilitiesText(currentApplication.serviceCapability || currentApplication.service_capability)" type="textarea" :rows="3" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.remark')">
              <el-input :value="currentApplication.remark || currentApplication.note || ''" type="textarea" :rows="3" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.applicantStatus')">
              <el-input v-model="currentApplication.statusText" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.applyTime')">
              <el-input :value="formatDateTime(currentApplication.applyTime)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.reviewer')">
              <el-input v-model="currentApplication.reviewer" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.reviewTime')">
              <el-input :value="currentApplication.reviewTime ? formatDateTime(currentApplication.reviewTime) : '-'" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item :label="$t('MemberRegistrationReview.reviewComment')" prop="reviewComment">
          <el-input 
            v-model="reviewForm.reviewComment" 
            type="textarea" 
            :rows="4" 
            :placeholder="$t('MemberRegistrationReview.reviewCommentPlaceholder')"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      </div>
      <template #footer>
        <el-button 
          @click="submitReview('approved')" 
          :loading="dialogLoading" 
          type="success">{{ $t('MemberRegistrationReview.approve') }}</el-button>
        <el-button 
          @click="submitReview('rejected')" 
          :loading="dialogLoading" 
          type="danger">{{ $t('MemberRegistrationReview.reject') }}</el-button>
        <el-button @click="closeDialog" type="default">{{ $t('MemberRegistrationReview.cancel') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import dialogMixin from '@/mixins/dialogMixin';
import countryMixin from '@/mixins/countryMixin';
import { passMemberApply, rejectMemberApply, getMemberApplyDetail, getSignUrl, updateDealerRating } from '@/api/api';

export default {
  mixins: [dialogMixin, countryMixin],
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      currentApplication: {},
      reviewForm: {
        reviewComment: ''
      },
      reviewRules: {
        reviewComment: [
          { required: true, message: this.$t('MemberRegistrationReview.reviewCommentRequired'), trigger: 'blur' },
          { min: 5, message: this.$t('MemberRegistrationReview.reviewCommentMinLength'), trigger: 'blur' }
        ]
      },
      dialogLoading: false,
      postCodeValidation: { valid: true, message: '' }
    };
  },
  mounted() {
    this.fetchCountryOptions();
  },
  methods: {
    getErrorMessage(errorCode, message) {
      if (errorCode) {
        const translated = this.$t(`MemberRegistrationReview.${errorCode}`);
        if (translated !== `MemberRegistrationReview.${errorCode}`) {
          return translated;
        }
      }
      return message;
    },

    getDealerRating(row) {
      return Number(row.dealer_rating) || Number(row.dealerRating) || 1;
    },

    async handleDealerRatingChange(row, rating) {
      if (rating > 3) {
        ElMessage.warning(this.$t('MemberRegistrationReview.dealerRatingMaxWarning'));
        return;
      }
      try {
        const response = await updateDealerRating(row.id, { dealer_rating: rating });
        if (response?.code === -1) {
          ElMessage.error(response?.message || this.$t('MemberRegistrationReview.dealerRatingUpdateFailed'));
        } else {
          row.dealer_rating = rating;
          ElMessage.success(this.$t('MemberRegistrationReview.dealerRatingUpdateSuccess'));
          this.$emit('refresh');
        }
      } catch (error) {
        console.error('更新评级失败:', error);
        ElMessage.error(this.$t('MemberRegistrationReview.dealerRatingUpdateFailed'));
      }
    },

    getStatusType(status) {
      const numericStatus = Number(status);
      const typeMap = {
        0: 'warning',
        1: 'success',
        2: 'danger',
        'pending': 'warning',
        'approved': 'success',
        'rejected': 'danger'
      };
      return typeMap[numericStatus] || typeMap[status] || 'info';
    },

    getStatusText(status) {
      const numericStatus = Number(status);
      const textMap = {
        0: this.$t('MemberRegistrationReview.statusPending'),
        1: this.$t('MemberRegistrationReview.statusApproved'),
        2: this.$t('MemberRegistrationReview.statusRejected'),
        'pending': this.$t('MemberRegistrationReview.statusPending'),
        'approved': this.$t('MemberRegistrationReview.statusApproved'),
        'rejected': this.$t('MemberRegistrationReview.statusRejected')
      };
      return textMap[numericStatus] || textMap[status] || status || '';
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    getCountryName(countryCode) {
      if (!countryCode) return '-';
      const country = this.countryOptions.find(c => c.code === countryCode);
      return country ? country.label : countryCode;
    },

    getAreaText(value) {
      const areaMap = {
        'less20': '< 20m²',
        '20-50': '20-50m²',
        '50-100': '50-100m²',
        'more100': '> 100m²',
        'warehouse_less50': '< 50m²',
        'warehouse_50-100': '50-100m²',
        'warehouse_100-200': '100-200m²',
        'warehouse_more200': '> 200m²',
        'warehouse_none': '无仓库'
      };
      return areaMap[value] || value || '-';
    },

    getStaffText(value) {
      const staffMap = {
        'staff_less5': '< 5 persons',
        'staff_5-10': '5-10 persons',
        'staff_10-20': '10-20 persons',
        'staff_more20': '> 20 persons'
      };
      return staffMap[value] || value || '-';
    },

    getServiceCapabilitiesText(capabilities) {
      if (!capabilities) return '-';
      const capArray = Array.isArray(capabilities) ? capabilities : capabilities.split(',');
      const capMap = {
        'hotLine': this.$t('MemberRegistration.serviceHotLine'),
        'service5d8h': this.$t('MemberRegistration.service5d8h'),
        'bikeRepair': this.$t('MemberRegistration.bikeRepair'),
        'electricalRepair': this.$t('MemberRegistration.electricalRepair'),
        'spareParts': this.$t('MemberRegistration.spareParts'),
        'onSiteService': this.$t('MemberRegistration.onSiteService')
      };
      return capArray.map(c => capMap[c] || c).join(', ');
    },

    getBusinessLicenseFileName(key) {
      if (!key) return '-';
      return key.split('/').pop();
    },

    openVatLink(type) {
      const urls = {
        eu: 'https://ec.europa.eu/taxation_customs/vies/#/vat-validation',
        nonEu: 'https://vatapp.net/vat-number-check',
        uk: 'https://www.tax.service.gov.uk/check-vat-number/enter-vat-details'
      };
      window.open(urls[type], '_blank');
    },

    async previewBusinessLicense(key) {
      if (!key) return;
      try {
        const result = await getSignUrl(key);
        let signedUrl = '';
        if (typeof result === 'string') {
          signedUrl = result;
        } else if (result && (result.url || result.data?.url)) {
          signedUrl = result.url || result.data?.url;
        }
        if (signedUrl && signedUrl.startsWith('http')) {
          window.open(signedUrl, '_blank');
        } else {
          ElMessage.error(this.$t('MemberRegistrationReview.previewFailed'));
        }
      } catch (error) {
        console.error('获取文件预览链接失败:', error);
        ElMessage.error(this.$t('MemberRegistrationReview.previewFailed'));
      }
    },

    getBusinessLicenseList(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return value.split(',').filter(v => v.trim());
    },

    getWorkWithAnandaText(item) {
      if (item.workWithAnanda === 'yes') return this.$t('MemberRegistrationReview.yes');
      if (item.workWithAnanda === 'no') return this.$t('MemberRegistrationReview.no');
      if (item.isUseAnanda === 1 || item.is_use_ananda === 1) return this.$t('MemberRegistrationReview.yes');
      return this.$t('MemberRegistrationReview.no');
    },

    getHowKnowText(value) {
      if (!value) return '-';
      const valArray = Array.isArray(value) ? value : value.split(',');
      const map = {
        'exhibition': this.$t('MemberRegistration.exhibition'),
        'magazines': this.$t('MemberRegistration.magazines'),
        'socialMedia': this.$t('MemberRegistration.socialMedia'),
        'supplier': this.$t('MemberRegistration.supplier')
      };
      return valArray.map(v => map[v] || v).join(', ');
    },

    getWhyChooseText(value) {
      if (!value) return '-';
      const valArray = Array.isArray(value) ? value : value.split(',');
      const map = {
        'qualityPrice': this.$t('MemberRegistration.qualityPrice'),
        'differentiation': this.$t('MemberRegistration.differentiation'),
        'supplierAdvice': this.$t('MemberRegistration.supplierAdvice'),
        'other': this.$t('MemberRegistration.other')
      };
      return valArray.map(v => map[v] || v).join(', ');
    },

    async handleView(row) {
      this.dialogLoading = true;
      try {
        const response = await getMemberApplyDetail(row.id);
        const detailData = response?.data || response;
        const auditStatus = detailData.audit_status !== undefined ? detailData.audit_status : (detailData.applicantStatus || detailData.status);
        const countryCode = detailData.country || detailData.country_code || row.country || '';
        this.currentApplication = {
          ...row,
          ...detailData,
          contactName: detailData.contactName || detailData.contact_name || row.contactName || '',
          storeName: detailData.storeName || detailData.store_name || row.storeName || '',
          country: countryCode,
          email: detailData.email || row.email || '',
          tel: detailData.tel || detailData.phone || row.tel || '',
          location: detailData.location || detailData.address || row.location || '',
          postCode: detailData.postCode || detailData.post_code || row.postCode || '',
          taxNumber: detailData.taxNumber || detailData.tax_number || row.taxNumber || '',
          officeArea: detailData.officeArea || detailData.office_area || row.officeArea || '',
          warehouse: detailData.warehouse || detailData.warehouse_area || row.warehouse || '',
          staff: detailData.staff || detailData.staff_count || row.staff || '',
          workWithAnanda: detailData.workWithAnanda || ((detailData.isUseAnanda == 1 || detailData.is_use_ananda == 1) ? 'yes' : 'no') || row.workWithAnanda,
          howKnowAnanda: detailData.howKnowAnanda || detailData.know_channel || row.howKnowAnanda || '',
          whyChooseAnanda: detailData.whyChooseAnanda || detailData.cooperate_reason || row.whyChooseAnanda || '',
          applicantStatus: auditStatus,
          statusText: this.getStatusText(auditStatus),
          applyTime: detailData.applyTime || detailData.apply_time || row.applyTime || '',
          reviewer: detailData.audit_user_name || detailData.reviewer || detailData.audit_user_id || detailData.auditor || '',
          reviewTime: detailData.reviewTime || detailData.audit_time || row.reviewTime || '',
          website: detailData.website || row.website || '',
          businessLicense: detailData.businessLicense || detailData.business_license || row.businessLicense || '',
          subscribeNewsletter: detailData.subscribeNewsletter || detailData.subscribe_newsletter || row.subscribeNewsletter || '',
          serviceCapability: detailData.serviceCapability || detailData.service_capability || row.serviceCapability || '',
          additionalInfo: detailData.additionalInfo || detailData.additional_info || row.additionalInfo || '',
          remark: detailData.remark || detailData.note || row.remark || '',
          reviewComment: detailData.reviewComment || detailData.audit_comment || row.reviewComment || ''
        };
        if (!this.countriesLoaded) {
          await this.fetchCountryOptions();
        }
        this.postCodeValidation = this.validatePostalCode(this.currentApplication.country, this.currentApplication.postCode);
      } catch (error) {
        console.error('获取详情失败:', error);
        this.currentApplication = { ...row };
        this.postCodeValidation = { valid: true, message: '' };
        ElMessage.error(this.$t('MemberRegistrationReview.reviewFailed'));
      } finally {
        this.dialogLoading = false;
      }
      this.reviewForm = {
        reviewComment: this.currentApplication.reviewComment || ''
      };
      this.dialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
      this.currentApplication = {};
      this.reviewForm = {
        reviewComment: ''
      };
    },

    async submitReview(reviewResult) {
      try {
        if (!this.$refs.reviewFormRef) {
          ElMessage.error(this.$t('MemberRegistrationReview.formValidationError'));
          return;
        }
        
        await this.$refs.reviewFormRef.validate();
        
        this.dialogLoading = true;
        
        const applyId = this.currentApplication.id;
        const reviewComment = this.reviewForm.reviewComment;
        const reviewerName = localStorage.getItem('userName') || '';
        
        let response;
        if (reviewResult === 'approved') {
          response = await passMemberApply(applyId, { auditComment: reviewComment, reviewer: reviewerName });
        } else {
          response = await rejectMemberApply(applyId, { auditComment: reviewComment, reviewer: reviewerName });
        }
        
        const msg = this.getErrorMessage(response?.error_code, response?.message || response?.msg);
        if (response?.code === -1) {
          ElMessage.error(msg || this.$t('MemberRegistrationReview.reviewFailed'));
        } else {
          ElMessage.success(msg || this.$t('MemberRegistrationReview.reviewSuccess'));
          this.closeDialog();
          this.$emit('refresh');
        }
        
      } catch (error) {
        if (error !== false) {
          console.error('审核失败:', error);
          if (!(error && error.message === this.$t('MemberRegistrationReview.reviewCommentMinLength'))) {
            const status = error?.response?.status;
            if (status === 500) {
              ElMessage.error(this.$t('MemberRegistrationReview.reviewFailed'));
            } else {
              const errorMsg = this.getErrorMessage(error?.response?.data?.error_code, error?.response?.data?.message || error?.response?.data?.msg || error?.message || this.$t('MemberRegistrationReview.reviewFailed'));
              ElMessage.error(errorMsg);
            }
          }
        }
      } finally {
        this.dialogLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.table-container {
  margin: 20px;
}

.dealer-rating {
  display: inline-flex;
  gap: 2px;
}

.star {
  font-size: 20px;
  cursor: pointer;
  user-select: none;
  transition: transform 0.2s;
}

.star:hover {
  transform: scale(1.2);
}

.star-gold {
  color: #FFD700;
}

.star-gray {
  color: #D3D3D3;
}

.vat-buttons {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 180px;
}

.vat-buttons .el-button {
  width: 100%;
  justify-content: flex-start;
  padding-left: 12px;
  margin-left: 0 !important;
}
</style>